import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import no_img from '../../../assets/images/picture.png';
import { CircleDot, Edit, Eye, EyeOff, Plus, Search, X } from 'lucide-react';
import { DataTable, IDataTableColumn } from '../../../components/common/DataTable';
import { BlockCard, PageBody, PageContainer, PageHeader } from '../../../components/container';
import { Button as ShadcnButton } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import { Skeleton } from '../../../components/ui/common';
import { useLookupProductCategory } from '../../../components/shared/hoc-product-category/useLookupProductCategory';
import { ProductActionConfirmRequest } from '../../../context/models/products/ProductActionConfirmRequest';
import { ProductModel, dfProductModel } from '../../../context/models/products/ProductModel';
import { ProductSelectRequest, df_ProductSelectRequest } from '../../../context/models/products/ProductSelectRequest';
import { eProductStatus } from '../../../context/models/products/eProductStatus';
import { BasePaginationRespone } from '../../../context/base/BasePaginationRespone';
import { BaseShowDetailId } from '../../../context/base/BaseShowDetail';
import { eBaseActionStatus } from '../../../context/base/eBaseActionStatus';
import { ProductHeaderButton } from '../button-actions/ProductHeaderButton';
import { ChangeStatusConfirm } from '../change-status-confirm';
import FilterBox from '../filter-box';
import { history } from '../../../utils/history';
import { getProductStatusName } from '../../../context/models/products/eProductStatus';
import { NotifyHelper } from '../../../utils/NotifyHelper';
import { BulkUpdateProductModal, BulkUpdateProductDraft } from '../bulk-update/BulkUpdateProductModal';
import { rootApi } from '../../../context/api/rootApi';
import { ProductModel as ProductModelType } from '../../../context/models/products/ProductModel';

type ProductTableFilterState = ProductSelectRequest & {
	pageIndex?: number;
	pageSize?: number;
};

const parseNumber = (value: string | null | undefined, fallback: number) => {
	const parsed = Number.parseInt(value || '', 10);
	return Number.isFinite(parsed) ? parsed : fallback;
};

const buildFilterFromSearchParams = (searchParams: URLSearchParams, productStatus: eProductStatus, langCode: string): ProductTableFilterState => {
	return {
		...df_ProductSelectRequest,
		status: productStatus,
		langCode,
		searchString: searchParams.get('searchString') || '',
		catId: parseNumber(searchParams.get('catId'), 0),
		pageIndex: parseNumber(searchParams.get('pageIndex'), 1),
		pageSize: parseNumber(searchParams.get('pageSize'), 20),
	};
};

const buildSearchParamsFromFilter = (filter: ProductTableFilterState) => {
	const searchParams = new URLSearchParams();
	if (filter.searchString) searchParams.set('searchString', filter.searchString);
	if (filter.catId > 0) searchParams.set('catId', String(filter.catId));
	if (filter.pageIndex && filter.pageIndex > 1) searchParams.set('pageIndex', String(filter.pageIndex));
	if (filter.pageSize && filter.pageSize !== 20) searchParams.set('pageSize', String(filter.pageSize));
	return searchParams;
};

type IProductTablePageProps = {
	titlePage: string;
	productStatus: eProductStatus;
	langCode: string;
	isOwner: boolean;
	status: eBaseActionStatus;
	paginationResponse: BasePaginationRespone<ProductModel>;
	selectedIds: number[];
	showDetail: BaseShowDetailId<number>;
	showConfirm: ProductActionConfirmRequest;
	handleReloadData: (request: ProductSelectRequest) => void;
	handleGetDetail: (id: number) => void;
	handleSaveData: (data: ProductModel) => void;
	handleDeleteData: (data: number[]) => void;
	onChangeSelectedIds: (ids: number[]) => void;
	handleChangeStatus: (data: ProductActionConfirmRequest) => void;
	onShowDetail: (data: BaseShowDetailId<number>) => void;
	onShowConfirm: (data: ProductActionConfirmRequest) => void;
};

const ProductTable = (props: IProductTablePageProps) => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { product_category_all } = useLookupProductCategory();
	const [showFilter, setShowFilter] = useState(false);
	const location = useLocation();
	const {
		titlePage,
		langCode,
		productStatus,
		isOwner,
		status,
		selectedIds,
		showConfirm,
		paginationResponse,
		handleReloadData,
		handleChangeStatus,
		onChangeSelectedIds,
		onShowConfirm,
	} = props;
	const { listDatas, pagination } = paginationResponse;
	const [showBulkUpdate, setShowBulkUpdate] = useState(false);
	const [tableData, setTableData] = useState<ProductModel[]>(listDatas || []);
	const [bulkProducts, setBulkProducts] = useState<ProductModelType[]>([]);
	const [isBulkLoading, setIsBulkLoading] = useState(false);
	const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);
	const [bulkProgressText, setBulkProgressText] = useState('');
	const [filter, setFilter] = useState<ProductTableFilterState>(() => buildFilterFromSearchParams(searchParams, productStatus, langCode));

	useEffect(() => {
		setTableData(listDatas || []);
	}, [listDatas]);

	useEffect(() => {
		return () => {
			onChangeSelectedIds([]);
		};
	}, [onChangeSelectedIds]);
	const selectedCount = selectedIds.length;

	const handleAddEditAction = (id?: string) => {
		navigate(`${location.pathname}/edit/${id || '0'}`, {
			replace: false,
			state: {
				returnTo: `${location.pathname}${location.search}`,
			},
		});
	};

	const handleReloadDataWithFilter = useCallback(() => {
		setShowFilter(false);
		handleReloadData(filter);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filter]);

	useEffect(() => {
		setSearchParams(buildSearchParamsFromFilter(filter), { replace: true });
	}, [filter, setSearchParams]);

	useEffect(() => {
		setFilter((current) => {
			const nextFilter = buildFilterFromSearchParams(searchParams, productStatus, langCode);
			const currentQuery = buildSearchParamsFromFilter(current).toString();
			const nextQuery = buildSearchParamsFromFilter(nextFilter).toString();
			return currentQuery === nextQuery ? current : nextFilter;
		});
	}, [searchParams, productStatus, langCode]);

	const handleOpenBulkUpdate = () => {
		if (selectedCount <= 0) {
			NotifyHelper.Warning('Hãy chọn ít nhất 1 sản phẩm để cập nhật hàng loạt.');
			return;
		}
		if (selectedCount > 10) {
			NotifyHelper.Warning('Mỗi lần chỉ được cập nhật tối đa 10 sản phẩm.');
			return;
		}
		setShowBulkUpdate(true);
	};

	useEffect(() => {
		if (!showBulkUpdate) return;
		let mounted = true;
		setIsBulkLoading(true);
		Promise.all(
			selectedIds.slice(0, 10).map(async (productId) => {
				const response: any = await rootApi.product.GetByIdAsync(productId);
				const detail: ProductModel | undefined = response?.result || response?.data?.result || response?.data;
				const fallback = tableData.find((item) => item.id === productId);
				const product: ProductModel = {
					...dfProductModel,
					...(fallback || {}),
					...(detail || {}),
					id: productId,
					name: (detail?.name || fallback?.name || '').trim() || `Sản phẩm #${productId}`,
					sku: detail?.sku || fallback?.sku || '',
				};
				return product;
			})
		)
			.then((items) => {
				if (!mounted) return;
				setBulkProducts(items);
			})
			.catch(() => {
				if (!mounted) return;
				NotifyHelper.Error('Không tải được dữ liệu chi tiết sản phẩm để cập nhật hàng loạt.');
			})
			.finally(() => {
				if (!mounted) return;
				setIsBulkLoading(false);
			});
		return () => {
			mounted = false;
		};
	}, [showBulkUpdate, selectedIds, tableData]);

	const handleApplyBulkUpdate = (draft: BulkUpdateProductDraft) => {
		if (isBulkSubmitting) return;
		if (selectedCount <= 0) {
			NotifyHelper.Warning('Hãy chọn ít nhất 1 sản phẩm để cập nhật hàng loạt.');
			return;
		}
		if (selectedCount > 10) {
			NotifyHelper.Warning('Mỗi lần chỉ được cập nhật tối đa 10 sản phẩm.');
			return;
		}

		const queueProducts = bulkProducts.slice(0, 10);
		if (queueProducts.length === 0) {
			NotifyHelper.Warning('Không có dữ liệu chi tiết để cập nhật.');
			return;
		}

		setIsBulkSubmitting(true);
		setBulkProgressText(`Đang cập nhật 0/${queueProducts.length} sản phẩm...`);

		void (async () => {
			let successCount = 0;
			let failureCount = 0;
			const failedNames: string[] = [];

			for (let index = 0; index < queueProducts.length; index += 1) {
				const product = queueProducts[index];
				const productDraft = draft[product.id];
				const hasChange = Boolean(
					(productDraft?.price !== undefined && productDraft.price !== product.price) ||
					(productDraft?.priceOnSale !== undefined && productDraft.priceOnSale !== product.priceOnSale) ||
					(productDraft?.summary !== undefined && productDraft.summary !== product.summary)
				);

				setBulkProgressText(`Đang cập nhật ${index + 1}/${queueProducts.length}: ${product.name}`);

				if (!hasChange) continue;

				const payload: ProductModel = {
					...product,
					price: productDraft?.price ?? product.price,
					priceOnSale: productDraft?.priceOnSale ?? product.priceOnSale,
					summary: productDraft?.summary ?? product.summary,
					actionStatus: eProductStatus.EDITED,
				};

				const response = await rootApi.product.PutAsync(payload);
				if (response.isSuccess) {
					successCount += 1;
					setTableData((current) =>
						current.map((item) => (item.id === payload.id ? { ...item, ...payload } : item))
					);
				} else {
					failureCount += 1;
					failedNames.push(product.name);
				}
			}

			setShowBulkUpdate(false);
			onChangeSelectedIds([]);
			await handleReloadData(filter);
			setIsBulkSubmitting(false);
			setBulkProgressText('');

			if (failureCount > 0) {
				NotifyHelper.Warning(`Cập nhật xong ${successCount} sản phẩm, thất bại ${failureCount} sản phẩm: ${failedNames.join(', ')}`);
				return;
			}

			NotifyHelper.Success(`Đã cập nhật ${successCount} sản phẩm thành công.`);
		})();
	};

	useEffect(() => {
		handleReloadDataWithFilter();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filter]);

	useEffect(() => {
		if (status === eBaseActionStatus.reload) handleReloadDataWithFilter();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const columns = useMemo((): IDataTableColumn<ProductModel>[] => {
		const statusTone: Record<string, string> = {
			PUBLISHED: 'border-emerald-300/70 bg-emerald-50 text-emerald-700',
			DRAFT: 'border-slate-300/70 bg-slate-100 text-slate-700',
			REMOVED: 'border-amber-300/70 bg-amber-50 text-amber-700',
			DELETED: 'border-rose-300/70 bg-rose-50 text-rose-700',
			RESTORE: 'border-teal-300/70 bg-teal-50 text-teal-700',
			EDITED: 'border-indigo-300/70 bg-indigo-50 text-indigo-700',
		};

		return [
			{
				id: 'image',
				accessorKey: 'image',
				header: 'Ảnh',
				size: 100,
				cell: ({ row }) => (
					<div className='user-card'>
						<div className='product-avatar bg-white border border-light hidden md:block'>
							<img src={row.original.image?.length > 0 ? row.original.image : no_img} alt='ảnh đại diện' />
						</div>
					</div>
				),
			},
			{ id: 'sku', accessorKey: 'sku', header: 'SKU', size: 100 },
			{ id: 'name', accessorKey: 'name', header: 'Sản phẩm', size: 300 },
			{
				id: 'catId',
				accessorKey: 'catId',
				header: 'Danh mục',
				size: 150,
				cell: ({ row }) => {
					const category = product_category_all.find((c) => c.id === row.original.catId);
					return category?.name || '-';
				},
			},
			{
				id: 'price',
				accessorKey: 'price',
				header: 'Giá bán',
				size: 150,
				cell: ({ row }) => new Intl.NumberFormat('vi-VN').format(Number(row.original.price || 0)),
			},
			{
				id: 'priceOnSale',
				accessorKey: 'priceOnSale',
				header: 'Giá giảm',
				size: 150,
				cell: ({ row }) => new Intl.NumberFormat('vi-VN').format(Number(row.original.priceOnSale || 0)),
			},
			{
				id: 'status',
				accessorKey: 'status',
				header: 'Trạng thái',
				size: 150,
				cell: ({ row }) => {
					const statusValue = String(row.original.status || 'DRAFT');
					return (
						<Badge
							variant='outline'
							className={`inline-flex min-w-[116px] items-center justify-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusTone[statusValue] || 'border-muted bg-muted/30 text-foreground/80'}`}
						>
							<CircleDot className='h-3 w-3' />
							{getProductStatusName(statusValue)}
						</Badge>
					);
				},
			},
			{
				id: 'visibility',
				header: 'Ẩn/Hiện',
				size: 90,
				cell: ({ row }) => {
					const statusValue = String(row.original.status || 'DRAFT');
					const isVisible = statusValue === eProductStatus.PUBLISHED;
					return (
						<TooltipProvider delayDuration={150}>
							<Tooltip>
								<TooltipTrigger asChild>
									<button
										type='button'
										className={isVisible
											? 'inline-flex h-8 w-8 items-center justify-center rounded-full border border-emerald-300/70 bg-emerald-50 text-emerald-700'
											: 'inline-flex h-8 w-8 items-center justify-center rounded-full border border-amber-300/70 bg-amber-50 text-amber-700'}
										aria-label={isVisible ? 'Đang hiển thị' : 'Đang ẩn'}
									>
										{isVisible ? <Eye className='h-3.5 w-3.5' /> : <EyeOff className='h-3.5 w-3.5' />}
									</button>
								</TooltipTrigger>
								<TooltipContent>
									{isVisible ? 'Hiển thị công khai' : 'Đang ẩn khỏi giao diện'}
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					);
				},
			},
			{
				id: 'actions',
				header: 'Sửa',
				size: 80,
				cell: ({ row }) => (
					<TooltipProvider delayDuration={150}>
						<Tooltip>
							<TooltipTrigger asChild>
								<ShadcnButton
									variant='outline'
									size='icon'
									onClick={(e) => {
										e.stopPropagation();
										handleAddEditAction(String(row.original.id));
									}}
									title='Sửa'
								>
									<Edit className='h-4 w-4' />
								</ShadcnButton>
							</TooltipTrigger>
							<TooltipContent>
								Sửa
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				),
			},
		];
	}, [product_category_all]);

	const statusLegend = (
		<div className='flex flex-wrap items-center gap-2 rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-xs'>
			<span className='font-medium text-foreground/80'>Chú thích trạng thái:</span>
			<span className='inline-flex items-center gap-1 text-emerald-700'><span className='h-2 w-2 rounded-full bg-emerald-500' />Đang bán</span>
			<span className='inline-flex items-center gap-1 text-amber-700'><span className='h-2 w-2 rounded-full bg-amber-500' />Ẩn/Ngưng bán</span>
			<span className='inline-flex items-center gap-1 text-slate-700'><span className='h-2 w-2 rounded-full bg-slate-500' />Nháp/Nội bộ</span>
		</div>
	);

	return (
		<>
			<PageContainer>
				<PageHeader title={`Danh sách ${titlePage}`} description='Theo dõi nhanh hiệu suất danh mục, giá và trạng thái để xử lý theo ưu tiên.'>
					<div className='flex flex-wrap items-center gap-2 justify-end'>
						<ShadcnButton
							onClick={() => onChangeSelectedIds([])}
							variant='outline'
							className='gap-2'
							disabled={selectedCount <= 0}
							title='Bỏ chọn tất cả sản phẩm đang tick'
						>
							<X className='h-4 w-4' />
							Bỏ chọn tất cả
						</ShadcnButton>
						<ProductHeaderButton
							isOwner={isOwner}
							productStatus={productStatus}
							selectedIds={selectedIds}
							onActionConfirm={(actionChange, selectedIds) =>
								onShowConfirm({ isShow: true, actionStatus: actionChange, ids: selectedIds })
							}
						/>
						<ShadcnButton
							onClick={handleOpenBulkUpdate}
							variant='outline'
							className='gap-2'
							disabled={selectedCount <= 0 || selectedCount > 10}
							title={selectedCount > 10 ? 'Chọn tối đa 10 sản phẩm cho mỗi lần cập nhật' : 'Cập nhật tạm thời giá bán, giá khuyến mãi và đặc tả'}
						>
							<Edit className='h-4 w-4' />
							Cập nhật hàng loạt
						</ShadcnButton>
						<ShadcnButton
							onClick={() => handleAddEditAction()}
							className='gap-2'
						>
							<Plus className='h-4 w-4' />
							Tạo mới
						</ShadcnButton>
						<ShadcnButton
							onClick={() => setShowFilter(true)}
							variant='outline'
							className='gap-2'
						>
							<Search className='h-4 w-4' />
							Lọc sản phẩm
						</ShadcnButton>
					</div>
				</PageHeader>
				<PageBody>

					<BlockCard>
						<div className='mb-3'>
							{statusLegend}
						</div>
						{status === eBaseActionStatus.loading && (
							<div className='space-y-3'>
								{Array.from({ length: 5 }).map((_, i) => (
									<Skeleton key={i} className='h-12 w-full' />
								))}
							</div>
						)}
						{status !== eBaseActionStatus.loading && (
							<DataTable
								data={tableData}
								columns={columns}
								scrollStorageKey={`product-list-scroll:${location.pathname}${location.search}`}
								isLoading={status !== eBaseActionStatus.complete}
								searchMode='remote'
								enableRowSelection={true}
								rowSelection={selectedIds.reduce((acc, id) => ({ ...acc, [String(id)]: true }), {})}
								onRowSelectionChange={(nextSelection) => {
									const ids = Object.keys(nextSelection)
										.filter((key) => nextSelection[key])
										.map((key) => Number(key));
									onChangeSelectedIds(ids);
								}}
								onRowClick={(row) => handleAddEditAction(String(row.id))}
								pageIndex={(pagination?.currentPage || 1) - 1}
								pageCount={pagination?.totalPages || 1}
								pageSize={pagination?.pageSize || 20}
								totalRecords={pagination?.totalRecords || 0}
								onPageChange={(page) => setFilter((prev) => ({ ...prev, pageIndex: page + 1 }))}
								onPageSizeChange={(size) => setFilter((prev) => ({ ...prev, pageSize: size, pageIndex: 1 }))}
								height={window.innerHeight - 300}
								toolbar={{
									isShowSearch: true,
									searchPlaceholder: 'Tìm kiếm sản phẩm...',
									searchValue: filter.searchString,
									onSearchChange: (value) => setFilter((prev) => ({ ...prev, searchString: value }))
								}}
								onRefresh={handleReloadDataWithFilter}
								noDataText={filter.searchString ? 'Không tìm thấy sản phẩm phù hợp' : 'Không có sản phẩm'}
							/>
						)}
					</BlockCard>
				</PageBody>
				
			</PageContainer>
			{showConfirm && showConfirm.isShow && (
				<>
					<ChangeStatusConfirm
						isShow={showConfirm.isShow}
						onClose={() => onShowConfirm({ isShow: false, actionStatus: showConfirm.actionStatus, ids: [] })}
						onConfirmStatus={() => handleChangeStatus(showConfirm)}
						isLoading={status === eBaseActionStatus.loading}
						actionStatus={showConfirm.actionStatus}
						selectedIds={selectedIds}
					/>
				</>
			)}
			{showFilter && (
				<FilterBox
					filterModel={filter}
					isLoading={status === eBaseActionStatus.loading}
					isShow={showFilter}
					onClose={() => setShowFilter(false)}
					onSubmit={(filterChanged) => setFilter((prev) => ({ ...prev, ...filterChanged }))}
				/>
			)}
			<BulkUpdateProductModal
				open={showBulkUpdate}
				products={bulkProducts}
				isLoading={isBulkLoading}
				isSubmitting={isBulkSubmitting}
				progressText={bulkProgressText}
				onClose={() => setShowBulkUpdate(false)}
				onApply={handleApplyBulkUpdate}
			/>
		</>
	);
};

export default ProductTable;
